import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, Whisper, IconButton, Tooltip, Badge, SelectPicker, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { Link, LinksPaginationFilterRequest, LinkTypeInfoMap } from '@/models/link.model';
import { getPagedLinks } from '@/services/link.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import LinkDrawer from './link-drawer';
import EditIcon from '@rsuite/icons/Edit';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface LinkListProps{
  runningClubId?: number,
  encyclopediaItemId?: number,
};

const LinkList : React.FC<LinkListProps> = ({runningClubId, encyclopediaItemId}) => {
  const [filterReq, setFilterReq] = useState<LinksPaginationFilterRequest>(new LinksPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [idToEdit, setIdToEdit] = useState<number|undefined>(undefined);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: LinksPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ,
        encyclopediaItemId: encyclopediaItemId
      };
      const result: PagedResponseModel<Link> = await getPagedLinks(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching links:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIdToEdit(undefined);
    fetchData(filterReq);
  };
  
  const onEditItem = (id: number)=>{
    setIdToEdit(id);
    handleOpenDrawer();
  };
  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={handleOpenDrawer}>
          New Link
        </Button>
        <Stack spacing={6}>
          <SelectPicker
            label="Type"
            data={Object.values(LinkTypeInfoMap)}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre =>({ ...pre, type: dataKey ?? undefined}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={2} align="left" >
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey='name'/>
        </Column>
        <Column flexGrow={3} align="left" >
          <HeaderCell>Link</HeaderCell>
          <Cell>
            {(rowData: Link) => <a href={rowData.linkPath}>{rowData.linkPath}</a>}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey="type">
            {(rowData: Link) => <Badge content={LinkTypeInfoMap[rowData.type].label} color={LinkTypeInfoMap[rowData.type].color}/>}
          </Cell>
        </Column>   

        <Column flexGrow={1} fixed="right" verticalAlign="middle" >
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: Link) => 
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Link</Tooltip>}>
                <IconButton onClick={()=>onEditItem(rowData.id)} icon={<EditIcon />} circle size="sm" />
              </Whisper>
            }
          </Cell>
        </Column>
      </Table>
      <Divider/>
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <LinkDrawer 
        linkId={idToEdit}
        runningClubId={runningClubId} 
        encyclopediaItemId={encyclopediaItemId}
        open={openDrawer} 
        onClose={handleCloseDrawer} 
      />
    </PageContent>
  );
};

export default LinkList;
